import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaAngleDown } from "react-icons/fa6";

import { useMeQuery } from "@hd/api/auth/queries/useMeQuery";
import { useSectionCompaniesQuery } from "@hd/api/company/queries/useSectionCompaniesQuery";
import { ListItemDto } from "@hd/types/dto/shared/list-item.dto";
import { ListItem } from "@hd/types/shared/list-item";
import { SectionIds } from "@hd/types/shared/section-ids";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useAuthStore } from "../auth.store";

interface CompanySelectorProps {
  label: string;
  hasGroup?: boolean;
  selectedCompanyId: number | null;
  selectedCompanyChanged: (
    companyId: number | null,
    companyName: string | null,
  ) => void;
  sectionId: SectionIds;
}

function CompanySelector({
  label,
  hasGroup = false,
  selectedCompanyChanged,
  selectedCompanyId,
  sectionId,
}: CompanySelectorProps) {
  // const companies = useAuth((s) => s.loggedUserData?.activeCompanies || []);
  const isChangingGroup = useAuthStore((s) => s.isChangingGroup);
  const { data: meData } = useMeQuery();

  const monoCompanyId = meData?.isMonoCompanyWithId;
  const { data: sectionCompaniesData, isPending } = useSectionCompaniesQuery({
    sectionId,
    isMonoCompany: !!monoCompanyId,
    hasGroup,
  });
  // console.log("monoCompanyId:", monoCompanyId);
  // console.log("sectionCompaniesData:", sectionCompaniesData);

  const companies = useMemo<ListItemDto[]>(() => {
    return sectionCompaniesData || [];
  }, [sectionCompaniesData]);

  const [innerSelectedCompany, setInnerSelectedCompany] =
    useState<ListItem | null>(null);

  const onChangeHandler = useCallback(
    (company: ListItem | null) => {
      if (!company) {
        selectedCompanyChanged(null, null);
        return;
      }
      selectedCompanyChanged(company.id, company.name);
    },
    [selectedCompanyChanged],
  );

  useEffect(() => {
    if (isChangingGroup) {
      setInnerSelectedCompany(null);
      onChangeHandler(null);
      return;
    }

    let company = companies.find((c) => c.id === selectedCompanyId);

    // se non ho una company selezionata all'inizio, seleziono la prima e scateno l'evento di change per informare il padre
    if (!company) {
      company = companies[0];
    }

    if (company) {
      setInnerSelectedCompany(company);
      onChangeHandler(company);
    }
  }, [
    companies,
    selectedCompanyId,
    setInnerSelectedCompany,
    onChangeHandler,
    isChangingGroup,
  ]);

  return (
    <div className={`flex items-center ${monoCompanyId ? "hidden" : ""}`}>
      {label && <div className="mr-2 text-xs">{label}</div>}
      <div className="ml-auto">
        {!isPending && (
          <Listbox value={innerSelectedCompany} onChange={onChangeHandler}>
            <ListboxButton className={"font-bold flex items-center py-1 "}>
              <span className="mr-3">{innerSelectedCompany?.name}</span>
              <FaAngleDown className="ml-auto text-gray-400 text-xxs" />
            </ListboxButton>
            <ListboxOptions
              transition
              anchor="bottom start"
              style={{
                boxShadow: "0px 2px 4px 0px #00000040",
                zIndex: 10000,
              }}
              className={clsx(
                "bg-white min-w-40 transition duration-200 ease-out data-[closed]:opacity-0 [--anchor-gap:4px]",
              )}
            >
              {companies.map((company) => (
                <ListboxOption
                  key={company.id}
                  value={company}
                  className={clsx(
                    "company-option text-sm text-bodyText px-3 py-2 cursor-pointer data-[selected]:!bg-[#f2f2f2] data-[selected]:text-black hover:bg-gray-100 hover:text-black my-0.5 mx-0.5",
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {company.name}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        )}
      </div>
    </div>
  );
}

export default CompanySelector;
